import React, { useState, useEffect } from "react";
import axios from "axios";
import Header from "./Header";
import Sidebar from "./Sidebar";
import SubHeader from "./SubHeader";
import "../Style/Dashboard.css";
import "../Style/RoleManagement.css";
import { useNavigate } from "react-router-dom";
import {
  Edit2,
  Trash2,
  Building2,
  Users,
  ChevronDown,
  Search,
  Mail,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import Model from "./Model";
import { BASE_URL } from "../Apis/apiConfig";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const RoleManagement = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [commiteMember, setCommiteMember] = useState([]);
  const [allCommiteMembers, setAllCommiteMembers] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedMemberId, setSelectedMemberId] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("society");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const societyName = (localStorage.getItem("society") || "").trim();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCommitMemberForEdit, setSelectedCommitMemberForEdit] =
    useState(null);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const fetchCommite = async () => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(
        `${BASE_URL}/api/assign-role/get-all-commite-member`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            commiteMember: "COMMITEE MEMBER",
            societyName: societyName,
          },
        },
      );
      const payload = response.data;
      const arr = Array.isArray(payload) ? payload : payload ? [payload] : [];
      setAllCommiteMembers(arr);
      setCommiteMember([]);
      setSelectedMemberId("");
    } catch (error) {
      console.error("Error fetching committee members:", error);
      setAllCommiteMembers([]);
      setCommiteMember([]);
      setSelectedMemberId("");
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      console.error("No token found. Please log in first.");
      return;
    }

    axios
      .get(`${BASE_URL}/api/services/get-all-service`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          societyName: societyName,
        },
      })
      .then((response) => {
        setServices(response.data ?? []);
      })
      .catch((error) => {
        console.error("Error fetching services:", error);
        setServices([]);
      });

    fetchCommite();
  }, [societyName]);

  useEffect(() => {
    if (activeSection === "society") {
      fetchCommite();
    }
  }, [activeSection]);

  const fetchEmployees = async (companyName, societyNameParam) => {
    const token = localStorage.getItem("token");

    if (!token || !companyName || !societyNameParam) {
      return;
    }

    try {
      const response = await axios.get(
        `${BASE_URL}/api/employee/get-emp-by-company-name`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            companyName,
            societyName: societyNameParam,
          },
        },
      );

      setEmployees(response.data ?? []);
    } catch (error) {
      console.error("Error fetching employees", error);
      setEmployees([]);
    }
  };

  const handleDeleteEmployee = async (emp) => {
    if (window.confirm(`Are you sure you want to delete ${emp.employeeId}?`)) {
      try {
        const token = localStorage.getItem("token");
        await axios.delete(`${BASE_URL}/api/employee/delete-emp-save-archive`, {
          params: {
            employeeId: emp.employeeId,
            societyName: societyName,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        toast.success("Employee deleted and archived successfully.");
        setEmployees((prev) =>
          prev.filter((e) => e.employeeId !== emp.employeeId),
        );
      } catch (error) {
        console.error("Error deleting employee:", error);
        toast.error("Error deleting employee.");
      }
    }
  };

  const handleEditEmployee = async (emp) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${BASE_URL}/api/employee/get-employee-by-employeeId`,
        {
          params: { employeeId: emp.employeeId, societyName: societyName },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setSelectedEmployee(response.data ?? emp);
      setIsModalOpen(true);
    } catch (error) {
      console.error("Error fetching employee:", error);
      toast.error("Failed to fetch employee.");
    }
  };

  const handleSelectCommitteeMember = (assignRoleId) => {
    setSelectedMemberId(assignRoleId || "");
    if (!assignRoleId) {
      setCommiteMember([]);
      return;
    }
    const found = allCommiteMembers.filter(
      (m) => String(m.assignRoleId) === String(assignRoleId),
    );
    setCommiteMember(found.length ? found : []);
  };

  const updateUserInDB = async (user) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${BASE_URL}/api/assign-role/update-assignRole`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            assignRoleId: user.assignRoleId,
            roll: user.role,
            currentWrok: user.currentWork ?? user.currentWrok ?? false,
            societyName: societyName,
          },
        },
      );
      toast.success("Updated successfully.");
    } catch (error) {
      console.error("Update failed:", error);
      toast.error("Update failed.");
    }
  };

  const handleRoleChange = (idx, newRole) => {
    const updated = [...commiteMember];
    updated[idx].role = newRole || "Viewer";

    if (updated[idx].currentWork === undefined) {
      updated[idx].currentWork = false;
    }

    setCommiteMember(updated);

    updateUserInDB({
      assignRoleId: updated[idx].assignRoleId,
      role: updated[idx].role,
      currentWork: updated[idx].currentWork,
    });
  };

  const toggleUsingApp = (idx) => {
    const updated = [...commiteMember];
    updated[idx].currentWork = !updated[idx].currentWork;
    setCommiteMember(updated);
    updateUserInDB(updated[idx]);
  };

  const handleFieldChange = async (idx, field, value) => {
    const updatedEmployees = [...employees];
    const updatedUser = { ...updatedEmployees[idx], [field]: value };
    updatedEmployees[idx] = updatedUser;
    setEmployees(updatedEmployees);

    try {
      const token = localStorage.getItem("token");

      await axios.put(
        `${BASE_URL}/api/employee/update-employee-role`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            employeeId: updatedUser.employeeId,
            role: updatedUser.role,
            usingApp: updatedUser.usingApp,
            status: updatedUser.status,
            societyName: societyName,
          },
        },
      );
      toast.success("Updated employee role successfully.");
    } catch (error) {
      console.error("Failed to update employee role:", error);
      toast.error("Failed to update.");
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedEmployee(null);
    setSelectedCommitMemberForEdit(null);
  };

  const handleEditCommiteMemeber = (user) => {
    setSelectedCommitMemberForEdit(user);
    setIsModalOpen(true);
  };

  const handleDeleteAssignRole = async (user) => {
    if (
      window.confirm(`Are you sure you want to delete ${user.assignRoleId}?`)
    ) {
      try {
        const token = localStorage.getItem("token");
        await axios.delete(`${BASE_URL}/api/assign-role/delete-role`, {
          params: {
            assignRoleId: user.assignRoleId,
            societyName: societyName,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        toast.success("Assign Role deleted and archived successfully.");
        setCommiteMember((prev) =>
          prev.filter((e) => e.assignRoleId !== user.assignRoleId),
        );
        setAllCommiteMembers((prev) =>
          prev.filter((e) => e.assignRoleId !== user.assignRoleId),
        );
        setSelectedMemberId("");
      } catch (error) {
        console.error("Error deleting assign role:", error);
        toast.error("Error deleting Assign role.");
      }
    }
  };

  const toggleRowSelection = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id],
    );
  };

  const toggleSelectAll = () => {
    const currentData = getCurrentData();
    if (selectedRows.length === currentData.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(
        currentData.map(
          (item) => item.assignRoleId || item.employeeId || item.id,
        ),
      );
    }
  };

  const getCurrentData = () => {
    if (
      activeSection === "society" &&
      selectedMemberId &&
      commiteMember.length > 0
    ) {
      return commiteMember;
    } else if (
      activeSection === "services" &&
      selectedCompany &&
      employees.length > 0
    ) {
      return employees;
    }
    return [];
  };

  const getFilteredData = () => {
    const data = getCurrentData();
    if (!searchTerm) return data;

    return data.filter((item) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        item.memberName?.toLowerCase().includes(searchLower) ||
        item.employeeName?.toLowerCase().includes(searchLower) ||
        item.unitNo?.toLowerCase().includes(searchLower) ||
        item.role?.toLowerCase().includes(searchLower) ||
        item.wing?.toLowerCase().includes(searchLower)
      );
    });
  };

  const getPaginatedData = () => {
    const filtered = getFilteredData();
    const startIndex = (currentPage - 1) * entriesPerPage;
    const endIndex = startIndex + entriesPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  const handleBulkDelete = () => {
    if (selectedRows.length === 0) {
      toast.warning("No rows selected for deletion.");
      return;
    }
    if (
      window.confirm(
        `Are you sure you want to delete ${selectedRows.length} selected items?`,
      )
    ) {
      toast.success(`Deleted ${selectedRows.length} items successfully.`);
      setSelectedRows([]);
    }
  };

  const handleSendEmail = () => {
    if (selectedRows.length === 0) {
      toast.warning("No rows selected to send email.");
      return;
    }
    toast.info(
      `Email functionality would be sent to ${selectedRows.length} selected members.`,
    );
  };

  const totalPages = Math.ceil(getFilteredData().length / entriesPerPage);

  return (
    <div className="app-container">
      <Header toggleSidebar={toggleSidebar} />

      <div className="main-layout">
        <Sidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
        />

        <main className="content-area">
          <SubHeader />

          <div
            style={{
              padding: "24px",
              backgroundColor: "#f8fafc",
              // minHeight: "calc(100vh - 64px - 60px)",
              // border: "1px solid black",
            }}
          >
            <div
              style={{
                backgroundColor: "white",
                borderRadius: "12px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                border: "1px solid #e2e8f0",
                overflow: "hidden",
                marginBottom: "1rem",
              }}
            >
              {/* Tab Navigation */}
              <div
                style={{
                  backgroundColor: "white",
                  borderBottom: "1px solid #e2e8f0",
                  padding: "0 24px",
                  display: "flex",
                  gap: "2px",
                }}
              >
                <button
                  style={{
                    padding: "16px 24px",
                    border: "none",
                    backgroundColor:
                      activeSection === "society" ? "white" : "transparent",
                    color: activeSection === "society" ? "#0d6efd" : "#64748b",
                    fontSize: "14px",
                    fontWeight: activeSection === "society" ? "600" : "500",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    borderBottom:
                      activeSection === "society"
                        ? "3px solid #0d6efd"
                        : "3px solid transparent",
                    marginBottom: "-1px",
                  }}
                  onClick={() => {
                    setActiveSection("society");
                    setSelectedCompany("");
                    setSelectedMemberId("");
                    setCommiteMember([]);
                    setSelectedRows([]);
                  }}
                >
                  <Users size={16} />
                  Society Members
                  <span
                    style={{
                      backgroundColor:
                        activeSection === "society" ? "#0d6efd" : "#e2e8f0",
                      color: activeSection === "society" ? "white" : "#475569",
                      fontSize: "11px",
                      fontWeight: "600",
                      padding: "2px 6px",
                      borderRadius: "10px",
                      marginLeft: "4px",
                    }}
                  >
                    {allCommiteMembers.length}
                  </span>
                </button>

                <button
                  style={{
                    padding: "16px 24px",
                    border: "none",
                    backgroundColor:
                      activeSection === "services" ? "white" : "transparent",
                    color: activeSection === "services" ? "#0d6efd" : "#64748b",
                    fontSize: "14px",
                    fontWeight: activeSection === "services" ? "600" : "500",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    borderBottom:
                      activeSection === "services"
                        ? "3px solid #0d6efd"
                        : "3px solid transparent",
                    marginBottom: "-1px",
                  }}
                  onClick={() => {
                    setActiveSection("services");
                    setSelectedMemberId("");
                    setCommiteMember([]);
                    setSelectedRows([]);
                  }}
                >
                  <Building2 size={16} />
                  Service Providers
                  <span
                    style={{
                      backgroundColor:
                        activeSection === "services" ? "#0d6efd" : "#e2e8f0",
                      color: activeSection === "services" ? "white" : "#475569",
                      fontSize: "11px",
                      fontWeight: "600",
                      padding: "2px 6px",
                      borderRadius: "10px",
                      marginLeft: "4px",
                    }}
                  >
                    {services.length}
                  </span>
                </button>
              </div>

              {/* Main Content */}
              <div style={{ padding: "24px" }}>
                {/* Dropdown Selection Section */}
                <div
                  style={{
                    marginBottom: "24px",
                    padding: "20px",
                    backgroundColor: "#f8fafc",
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  {activeSection === "services" && (
                    <div style={{ maxWidth: "400px" }}>
                      <label
                        style={{
                          display: "block",
                          fontSize: "14px",
                          fontWeight: "500",
                          color: "#1e293b",
                          marginBottom: "8px",
                        }}
                      >
                        Select Service Provider
                      </label>
                      <div style={{ position: "relative" }} className=" ">
                        <select
                          style={{
                            width: "100%",
                            padding: "12px 16px",
                            paddingRight: "40px",
                            border: "1px solid #e2e8f0",
                            borderRadius: "8px",
                            fontSize: "14px",
                            backgroundColor: "white",
                            color: "#1e293b",
                            cursor: "pointer",
                            appearance: "none",
                          }}
                          onChange={(e) => {
                            const selected = e.target.value;
                            setSelectedCompany(selected);
                            setSelectedMemberId("");
                            setCommiteMember([]);
                            setSelectedRows([]);
                            fetchEmployees(selected, societyName);
                          }}
                          value={selectedCompany}
                        >
                          <option value="">Choose a Company...</option>
                          {services.map((provider, i) => (
                            <option key={i} value={provider.companyName}>
                              {provider.companyName}
                            </option>
                          ))}
                        </select>
                        <ChevronDown
                          size={16}
                          style={{
                            position: "absolute",
                            right: "16px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            pointerEvents: "none",
                            color: "#64748b",
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {activeSection === "society" && (
                    <div className="flex flex-row w-full justify-between">
                      <div>
                        <label
                          style={{
                            display: "block",
                            fontSize: "14px",
                            fontWeight: "500",
                            color: "#1e293b",
                            marginBottom: "8px",
                          }}
                        >
                          Select Committee Member
                        </label>
                        <div style={{ position: "relative" }}>
                          <select
                            style={{
                              width: "100%",
                              padding: "12px 16px",
                              paddingRight: "40px",
                              border: "1px solid #e2e8f0",
                              borderRadius: "8px",
                              fontSize: "14px",
                              backgroundColor: "white",
                              color: "#1e293b",
                              cursor: "pointer",
                              appearance: "none",
                            }}
                            onChange={(e) => {
                              const assignRoleId = e.target.value;
                              handleSelectCommitteeMember(assignRoleId);
                              setSelectedRows([]);
                            }}
                            value={selectedMemberId}
                          >
                            <option value="">Choose a Member...</option>
                            {allCommiteMembers.length > 0 ? (
                              allCommiteMembers.map((member) => (
                                <option
                                  key={member.assignRoleId}
                                  value={member.assignRoleId}
                                >
                                  {member.memberName ||
                                    member.name ||
                                    `Unit ${member.unitNo}`}
                                </option>
                              ))
                            ) : (
                              <option value="" disabled>
                                No committee members available
                              </option>
                            )}
                          </select>
                          <ChevronDown
                            size={16}
                            style={{
                              position: "absolute",
                              right: "16px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              pointerEvents: "none",
                              color: "#64748b",
                            }}
                          />
                        </div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: "12px",
                          flexWrap: "wrap",
                        }}
                      >
                        <button
                          className="h-10 mt-3 bg-blue-600 text-white hover:bg-blue-400"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "8px",
                            padding: "10px 20px",
                            borderRadius: "8px",
                            fontSize: "14px",
                            fontWeight: "500",
                            cursor: "pointer",

                            border: "1px solid #e2e8f0",

                            whiteSpace: "nowrap",
                          }}
                          onClick={() => fetchCommite()}
                        >
                          <RefreshCw size={16} />
                          Refresh
                        </button>

                        {/* <button
                          className="h-10 mt-3"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "8px",
                            padding: "10px 20px",
                            borderRadius: "8px",
                            fontSize: "14px",
                            fontWeight: "500",
                            cursor: "pointer",
                            backgroundColor: "#0d6efd",
                            border: "1px solid #0d6efd",
                            color: "white",
                            whiteSpace: "nowrap",
                          }}
                          onClick={handleSendEmail}
                        >
                          <Mail size={16} />
                          Send Email ({selectedRows.length})
                        </button> */}

                        <button
                          className="h-10 mt-3 text-white bg-red-300 hover:bg-red-500"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "8px",
                            padding: "10px 20px",
                            borderRadius: "8px",
                            fontSize: "14px",
                            fontWeight: "500",
                            cursor: "pointer",

                            border: "1px solid #e2e8f0",

                            whiteSpace: "nowrap",
                          }}
                          onClick={handleBulkDelete}
                        >
                          <Trash2 size={16} />
                          Bulk Delete
                        </button>

                        <button
                          className="h-10 mt-3"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "8px",
                            padding: "10px 20px",
                            borderRadius: "8px",
                            fontSize: "14px",
                            fontWeight: "500",
                            cursor: "pointer",
                            backgroundColor: "#dc2626",
                            border: "1px solid #dc2626",
                            color: "white",
                            whiteSpace: "nowrap",
                          }}
                          onClick={handleBulkDelete}
                        >
                          <Trash2 size={16} />
                          Selected Delete ({selectedRows.length})
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Table Controls - PROPERLY ALIGNED */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "20px",
                    flexWrap: "wrap",
                    gap: "16px",
                  }}
                >
                  {/* Left side - Header Label only */}
                  <div
                    style={{
                      fontWeight: "600",
                      color: "#1e293b",
                      fontSize: "14px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {activeSection === "society"
                      ? "SELECT COMMITTEE MEMBER"
                      : "SELECT SERVICE PROVIDER"}
                  </div>

                  {/* Right side - Search and Show entries */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "16px",
                    }}
                  >
                    <div style={{ position: "relative" }}>
                      <input
                        type="text"
                        placeholder="Search anything..."
                        style={{
                          padding: "10px 12px",
                          border: "1px solid #e2e8f0",
                          borderRadius: "8px",
                          fontSize: "14px",
                          backgroundColor: "white",
                          width: "250px",
                        }}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "14px",
                          color: "#64748b",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Show:
                      </span>
                      <select
                        style={{
                          padding: "8px 12px",
                          border: "1px solid #e2e8f0",
                          borderRadius: "6px",
                          fontSize: "14px",
                          backgroundColor: "white",
                          minWidth: "100px",
                        }}
                        value={entriesPerPage}
                        onChange={(e) =>
                          setEntriesPerPage(Number(e.target.value))
                        }
                      >
                        <option value="10">10 entries</option>
                        <option value="25">25 entries</option>
                        <option value="50">50 entries</option>
                        <option value="100">100 entries</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Main Table or Empty State */}
                {(activeSection === "services" &&
                  selectedCompany &&
                  employees.length > 0) ||
                (activeSection === "society" &&
                  selectedMemberId &&
                  commiteMember.length > 0) ? (
                  <>
                    <div
                      style={{
                        overflowX: "auto",
                        border: "1px solid #e2e8f0",
                        borderRadius: "8px",
                        backgroundColor: "white",
                        marginBottom: "20px",
                      }}
                    >
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          minWidth: "1200px",
                        }}
                      >
                        <thead>
                          <tr
                            style={{
                              backgroundColor: "#f8fafc",
                              borderBottom: "2px solid #e2e8f0",
                            }}
                          >
                            <th
                              style={{
                                padding: "16px",
                                fontWeight: "600",
                                color: "#475569",
                                fontSize: "13px",
                                textTransform: "uppercase",
                                letterSpacing: "0.05em",
                                textAlign: "left",
                                borderBottom: "1px solid #e2e8f0",
                                whiteSpace: "nowrap",
                                width: "50px",
                              }}
                            >
                              <input
                                type="checkbox"
                                style={{
                                  width: "18px",
                                  height: "18px",
                                  borderRadius: "4px",
                                  border: "2px solid #cbd5e0",
                                  cursor: "pointer",
                                  margin: "0 auto",
                                  display: "block",
                                }}
                                checked={
                                  selectedRows.length ===
                                  getCurrentData().length
                                }
                                onChange={toggleSelectAll}
                              />
                            </th>
                            <th
                              style={{
                                padding: "16px",
                                fontWeight: "600",
                                color: "#475569",
                                fontSize: "13px",
                                textTransform: "uppercase",
                                letterSpacing: "0.05em",
                                textAlign: "left",
                                borderBottom: "1px solid #e2e8f0",
                                whiteSpace: "nowrap",
                              }}
                            >
                              SR NO
                            </th>
                            {activeSection === "society" ? (
                              <>
                                <th className="border">Member Name</th>
                                <th className="border">Unit No</th>
                                <th className="border">Wing</th>
                                <th className="border">Role</th>
                                <th className="border">Current Work</th>
                                <th className="border">Actions</th>
                              </>
                            ) : (
                              <>
                                <th className="border">Employee Name</th>
                                <th className="border">Assigned Role</th>
                                <th className="border">App Access</th>
                                <th className="border">Status</th>
                                <th className="border">Actions</th>
                              </>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {getPaginatedData().map((item, index) => {
                            const globalIndex =
                              (currentPage - 1) * entriesPerPage + index + 1;
                            const itemId =
                              item.assignRoleId ||
                              item.employeeId ||
                              item.id ||
                              index;

                            return (
                              <tr
                                key={itemId}
                                style={{ borderBottom: "1px solid #f1f5f9" }}
                              >
                                <td
                                  style={{
                                    padding: "16px",
                                    textAlign: "center",
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    style={{
                                      width: "18px",
                                      height: "18px",
                                      borderRadius: "4px",
                                      border: "2px solid #cbd5e0",
                                      cursor: "pointer",
                                    }}
                                    checked={selectedRows.includes(itemId)}
                                    onChange={() => toggleRowSelection(itemId)}
                                  />
                                </td>
                                <td
                                  style={{
                                    padding: "16px",
                                    fontSize: "14px",
                                    color: "#1e293b",
                                  }}
                                >
                                  {globalIndex}
                                </td>

                                {activeSection === "society" ? (
                                  <>
                                    <td
                                      style={{
                                        padding: "16px",
                                        fontSize: "14px",
                                        color: "#1e293b",
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "12px",
                                        }}
                                      >
                                        <div
                                          style={{
                                            width: "36px",
                                            height: "36px",
                                            borderRadius: "50%",
                                            background:
                                              "linear-gradient(135deg, #0d6efd 0%, #0b5ed7 100%)",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color: "white",
                                            fontWeight: "600",
                                            fontSize: "14px",
                                          }}
                                        >
                                          {(item.memberName || item.name || "U")
                                            .charAt(0)
                                            .toUpperCase()}
                                        </div>
                                        <span>
                                          {item.memberName ||
                                            item.name ||
                                            "Unknown"}
                                        </span>
                                      </div>
                                    </td>
                                    <td
                                      style={{
                                        padding: "16px",
                                        fontSize: "14px",
                                        color: "#1e293b",
                                      }}
                                    >
                                      {item.unitNo || "-"}
                                    </td>
                                    <td
                                      style={{
                                        padding: "16px",
                                        fontSize: "14px",
                                        color: "#1e293b",
                                      }}
                                    >
                                      {item.wing || "-"}
                                    </td>
                                    <td
                                      style={{
                                        padding: "16px",
                                        fontSize: "14px",
                                        color: "#1e293b",
                                      }}
                                    >
                                      <select
                                        style={{
                                          padding: "8px 12px",
                                          border: "1px solid #e2e8f0",
                                          borderRadius: "6px",
                                          fontSize: "14px",
                                          backgroundColor: "white",
                                          width: "100%",
                                          cursor: "pointer",
                                          minWidth: "120px",
                                        }}
                                        value={item.role || ""}
                                        onChange={(e) =>
                                          handleRoleChange(
                                            index,
                                            e.target.value,
                                          )
                                        }
                                      >
                                        <option value="">Select Role</option>
                                        <option value="Maid">Maid</option>
                                        <option value="Security">
                                          Security
                                        </option>
                                        <option value="Guard">Guard</option>
                                        <option value="Cleaner">Cleaner</option>
                                        <option value="Electrician">
                                          Electrician
                                        </option>
                                      </select>
                                    </td>
                                    <td
                                      style={{
                                        padding: "16px",
                                        fontSize: "14px",
                                        color: "#1e293b",
                                      }}
                                    >
                                      <label
                                        style={{
                                          position: "relative",
                                          display: "inline-block",
                                          width: "44px",
                                          height: "24px",
                                        }}
                                      >
                                        <input
                                          type="checkbox"
                                          style={{
                                            opacity: 0,
                                            width: 0,
                                            height: 0,
                                          }}
                                          checked={!!item.currentWork}
                                          onChange={() => toggleUsingApp(index)}
                                        />
                                        <span
                                          style={{
                                            position: "absolute",
                                            cursor: "pointer",
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            backgroundColor: item.currentWork
                                              ? "#10b981"
                                              : "#e2e8f0",
                                            transition: ".4s",
                                            borderRadius: "34px",
                                          }}
                                        >
                                          <span
                                            style={{
                                              position: "absolute",
                                              content: '""',
                                              height: "16px",
                                              width: "16px",
                                              left: item.currentWork
                                                ? "24px"
                                                : "4px",
                                              bottom: "4px",
                                              backgroundColor: "white",
                                              transition: ".4s",
                                              borderRadius: "50%",
                                            }}
                                          />
                                        </span>
                                      </label>
                                    </td>
                                    <td
                                      style={{
                                        padding: "16px",
                                        fontSize: "14px",
                                        color: "#1e293b",
                                      }}
                                    >
                                      <div
                                        style={{ display: "flex", gap: "8px" }}
                                      >
                                        <button
                                          style={{
                                            width: "32px",
                                            height: "32px",
                                            borderRadius: "6px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            backgroundColor: "#dbeafe",
                                            color: "#1d4ed8",
                                            border: "1px solid #bfdbfe",
                                            cursor: "pointer",
                                          }}
                                          onClick={() =>
                                            handleEditCommiteMemeber(item)
                                          }
                                          title="Edit"
                                        >
                                          <Edit2 size={16} />
                                        </button>
                                        <button
                                          style={{
                                            width: "32px",
                                            height: "32px",
                                            borderRadius: "6px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            backgroundColor: "#fee2e2",
                                            color: "#dc2626",
                                            border: "1px solid #fecaca",
                                            cursor: "pointer",
                                          }}
                                          onClick={() =>
                                            handleDeleteAssignRole(item)
                                          }
                                          title="Delete"
                                        >
                                          <Trash2 size={16} />
                                        </button>
                                      </div>
                                    </td>
                                  </>
                                ) : (
                                  <>
                                    <td
                                      style={{
                                        padding: "16px",
                                        fontSize: "14px",
                                        color: "#1e293b",
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "12px",
                                        }}
                                      >
                                        <div
                                          style={{
                                            width: "36px",
                                            height: "36px",
                                            borderRadius: "50%",
                                            background:
                                              "linear-gradient(135deg, #0d6efd 0%, #0b5ed7 100%)",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color: "white",
                                            fontWeight: "600",
                                            fontSize: "14px",
                                          }}
                                        >
                                          {(item.employeeName || "E")
                                            .charAt(0)
                                            .toUpperCase()}
                                        </div>
                                        <span>
                                          {item.employeeName || "Unknown"}
                                        </span>
                                      </div>
                                    </td>
                                    <td
                                      style={{
                                        padding: "16px",
                                        fontSize: "14px",
                                        color: "#1e293b",
                                      }}
                                    >
                                      <select
                                        style={{
                                          padding: "8px 12px",
                                          border: "1px solid #e2e8f0",
                                          borderRadius: "6px",
                                          fontSize: "14px",
                                          backgroundColor: "white",
                                          width: "100%",
                                          cursor: "pointer",
                                          minWidth: "120px",
                                        }}
                                        value={item.role || ""}
                                        onChange={(e) =>
                                          handleFieldChange(
                                            index,
                                            "role",
                                            e.target.value,
                                          )
                                        }
                                      >
                                        <option value="">Select Role</option>
                                        <option value="Maid">Maid</option>
                                        <option value="Security">
                                          Security
                                        </option>
                                        <option value="Guard">Guard</option>
                                        <option value="Cleaner">Cleaner</option>
                                        <option value="Electrician">
                                          Electrician
                                        </option>
                                      </select>
                                    </td>
                                    <td
                                      style={{
                                        padding: "16px",
                                        fontSize: "14px",
                                        color: "#1e293b",
                                      }}
                                    >
                                      <label
                                        style={{
                                          position: "relative",
                                          display: "inline-block",
                                          width: "44px",
                                          height: "24px",
                                        }}
                                      >
                                        <input
                                          type="checkbox"
                                          style={{
                                            opacity: 0,
                                            width: 0,
                                            height: 0,
                                          }}
                                          checked={item.usingApp || false}
                                          onChange={() =>
                                            handleFieldChange(
                                              index,
                                              "usingApp",
                                              !item.usingApp,
                                            )
                                          }
                                        />
                                        <span
                                          style={{
                                            position: "absolute",
                                            cursor: "pointer",
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            backgroundColor: item.usingApp
                                              ? "#10b981"
                                              : "#e2e8f0",
                                            transition: ".4s",
                                            borderRadius: "34px",
                                          }}
                                        >
                                          <span
                                            style={{
                                              position: "absolute",
                                              content: '""',
                                              height: "16px",
                                              width: "16px",
                                              left: item.usingApp
                                                ? "24px"
                                                : "4px",
                                              bottom: "4px",
                                              backgroundColor: "white",
                                              transition: ".4s",
                                              borderRadius: "50%",
                                            }}
                                          />
                                        </span>
                                      </label>
                                    </td>
                                    <td
                                      style={{
                                        padding: "16px",
                                        fontSize: "14px",
                                        color: "#1e293b",
                                      }}
                                    >
                                      <label
                                        style={{
                                          position: "relative",
                                          display: "inline-block",
                                          width: "44px",
                                          height: "24px",
                                        }}
                                      >
                                        <input
                                          type="checkbox"
                                          style={{
                                            opacity: 0,
                                            width: 0,
                                            height: 0,
                                          }}
                                          checked={item.status || false}
                                          onChange={() =>
                                            handleFieldChange(
                                              index,
                                              "status",
                                              !item.status,
                                            )
                                          }
                                        />
                                        <span
                                          style={{
                                            position: "absolute",
                                            cursor: "pointer",
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            backgroundColor: item.status
                                              ? "#10b981"
                                              : "#e2e8f0",
                                            transition: ".4s",
                                            borderRadius: "34px",
                                          }}
                                        >
                                          <span
                                            style={{
                                              position: "absolute",
                                              content: '""',
                                              height: "16px",
                                              width: "16px",
                                              left: item.status
                                                ? "24px"
                                                : "4px",
                                              bottom: "4px",
                                              backgroundColor: "white",
                                              transition: ".4s",
                                              borderRadius: "50%",
                                            }}
                                          />
                                        </span>
                                      </label>
                                    </td>
                                    <td
                                      style={{
                                        padding: "16px",
                                        fontSize: "14px",
                                        color: "#1e293b",
                                      }}
                                    >
                                      <div
                                        style={{ display: "flex", gap: "8px" }}
                                      >
                                        <button
                                          style={{
                                            width: "32px",
                                            height: "32px",
                                            borderRadius: "6px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            backgroundColor: "#dbeafe",
                                            color: "#1d4ed8",
                                            border: "1px solid #bfdbfe",
                                            cursor: "pointer",
                                          }}
                                          onClick={() =>
                                            handleEditEmployee(item)
                                          }
                                          title="Edit"
                                        >
                                          <Edit2 size={16} />
                                        </button>
                                        <button
                                          style={{
                                            width: "32px",
                                            height: "32px",
                                            borderRadius: "6px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            backgroundColor: "#fee2e2",
                                            color: "#dc2626",
                                            border: "1px solid #fecaca",
                                            cursor: "pointer",
                                          }}
                                          onClick={() =>
                                            handleDeleteEmployee(item)
                                          }
                                          title="Delete"
                                        >
                                          <Trash2 size={16} />
                                        </button>
                                      </div>
                                    </td>
                                  </>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "20px",
                          borderTop: "1px solid #e2e8f0",
                          backgroundColor: "white",
                        }}
                      >
                        <div style={{ fontSize: "14px", color: "#64748b" }}>
                          Showing {(currentPage - 1) * entriesPerPage + 1} to{" "}
                          {Math.min(
                            currentPage * entriesPerPage,
                            getFilteredData().length,
                          )}{" "}
                          of {getFilteredData().length} members
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <button
                            style={{
                              width: "36px",
                              height: "36px",
                              borderRadius: "6px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "white",
                              border: "1px solid #e2e8f0",
                              color: "#475569",
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              setCurrentPage((prev) => Math.max(prev - 1, 1))
                            }
                            disabled={currentPage === 1}
                          >
                            <ChevronLeft size={18} />
                          </button>

                          {Array.from(
                            { length: Math.min(5, totalPages) },
                            (_, i) => {
                              let pageNum;
                              if (totalPages <= 5) {
                                pageNum = i + 1;
                              } else if (currentPage <= 3) {
                                pageNum = i + 1;
                              } else if (currentPage >= totalPages - 2) {
                                pageNum = totalPages - 4 + i;
                              } else {
                                pageNum = currentPage - 2 + i;
                              }

                              return (
                                <button
                                  key={pageNum}
                                  style={{
                                    padding: "8px 12px",
                                    borderRadius: "6px",
                                    fontSize: "14px",
                                    cursor: "pointer",
                                    minWidth: "36px",
                                    textAlign: "center",
                                    backgroundColor:
                                      currentPage === pageNum
                                        ? "#0d6efd"
                                        : "white",
                                    color:
                                      currentPage === pageNum
                                        ? "white"
                                        : "#475569",
                                    border: "1px solid #e2e8f0",
                                  }}
                                  onClick={() => setCurrentPage(pageNum)}
                                >
                                  {pageNum}
                                </button>
                              );
                            },
                          )}

                          <button
                            style={{
                              width: "36px",
                              height: "36px",
                              borderRadius: "6px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "white",
                              border: "1px solid #e2e8f0",
                              color: "#475569",
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              setCurrentPage((prev) =>
                                Math.min(prev + 1, totalPages),
                              )
                            }
                            disabled={currentPage === totalPages}
                          >
                            <ChevronRight size={18} />
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "60px 20px",
                      backgroundColor: "white",
                      borderRadius: "8px",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <div style={{ marginBottom: "16px" }}>
                      <Search size={48} color="#94a3b8" />
                    </div>
                    <h3
                      style={{
                        fontSize: "18px",
                        color: "#1e293b",
                        margin: "16px 0 8px 0",
                      }}
                    >
                      {activeSection === "society"
                        ? "Select a Committee Member"
                        : selectedCompany
                          ? "No Employees Found"
                          : "Select a Service Provider"}
                    </h3>
                    <p
                      style={{
                        color: "#64748b",
                        fontSize: "14px",
                        margin: 0,
                      }}
                    >
                      {activeSection === "society"
                        ? "Please select a committee member from the dropdown above to view or assign roles."
                        : selectedCompany
                          ? "No employees found for the selected service provider."
                          : "Please select a service provider company from the dropdown above."}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Modal */}
          {isModalOpen && (selectedEmployee || selectedCommitMemberForEdit) && (
            <Model
              employee={selectedEmployee || selectedCommitMemberForEdit}
              onClose={handleModalClose}
            />
          )}
        </main>
      </div>
    </div>
  );
};

export default RoleManagement;
